import type { PlaceLayer } from '../types/Place';
import { defaultTheme } from '../config/theme.config';

/**
 * Centralized layer configuration - derived from theme configuration.
 *
 * Color values, labels, and layer definitions flow from src/config/theme.config.ts
 * (single source of truth). This module augments them with UI-specific fields
 * (bgClass, markerClass) for component consumption.
 *
 * When adding a new layer:
 * 1. Add the layer to PlaceLayer type in src/types/Place.ts
 * 2. Add configuration to theme.config.ts layers array
 * 3. Add CSS custom properties to src/index.css :root
 * 4. Add layer-specific CSS classes if needed (marker styles, etc.)
 */

export interface LayerColorConfig {
  /** Primary marker color (hex) */
  primary: string;
  /** RGB components for CSS custom properties (e.g., "139, 92, 246") */
  primaryRgb: string;
  /** Muted color for visited markers (hex) */
  visited: string;
  /** RGB components for visited color */
  visitedRgb: string;
  /** Highlighted beacon color (hex) */
  highlighted: string;
}

export interface LayerConfig {
  /** Layer identifier matching PlaceLayer type */
  id: PlaceLayer;
  /** Full display label */
  label: string;
  /** Short label for mobile/compact views */
  shortLabel: string;
  /** Color configuration */
  colors: LayerColorConfig;
  /** Tailwind background color class */
  bgClass: string;
  /** CSS class name for marker styling (e.g., "marker-layer-travels") */
  markerClass: string;
  /** Whether this layer uses dashed border style (for wishlist) */
  dashedBorder?: boolean;
}

/**
 * Configuration for all place layers.
 * Order determines display order in selectors and legends.
 *
 * Derived from theme.config.ts layers with additional UI fields.
 */
export const LAYER_CONFIGS: readonly LayerConfig[] = defaultTheme.layers.map(layer => ({
  id: layer.id as PlaceLayer,
  label: layer.label,
  shortLabel: layer.shortLabel,
  colors: {
    primary: layer.colors.primary,
    primaryRgb: layer.colors.primaryRgb,
    visited: layer.colors.visited,
    visitedRgb: layer.colors.visitedRgb,
    highlighted: layer.colors.highlighted,
  },
  bgClass: `bg-[${layer.colors.primary}]`,
  markerClass: `marker-layer-${layer.id}`,
  ...(layer.dashedBorder ? { dashedBorder: true } : {}),
}));

/**
 * Map of layer ID to configuration for quick lookup.
 */
export const LAYER_CONFIG_MAP: Record<PlaceLayer, LayerConfig> = Object.fromEntries(
  LAYER_CONFIGS.map(config => [config.id, config])
) as Record<PlaceLayer, LayerConfig>;

/**
 * Get layer configuration by ID.
 */
export function getLayerConfig(layerId: PlaceLayer): LayerConfig {
  return LAYER_CONFIG_MAP[layerId];
}

/**
 * Legacy exports for backwards compatibility with existing code.
 * These will be deprecated in favor of LAYER_CONFIGS.
 */
export const LAYER_COLORS = Object.fromEntries(
  LAYER_CONFIGS.map(config => [config.id, config.colors])
) as Record<PlaceLayer, LayerColorConfig>;

export const LAYER_COLOR_CLASSES = Object.fromEntries(
  LAYER_CONFIGS.map(config => [config.id, config.bgClass])
) as Record<PlaceLayer, string>;

export type LayerColorKey = PlaceLayer;
